"use client"

import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowLeft, ShoppingCart, Plus, Minus, Star, Package } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { useGetDealQuery } from "@/store"
import { useCart } from "@/context/CartContext"
import { toast } from "sonner"
import type { DealSelections, ComponentType } from "@/types/deals"
import { SEO } from "@/components/seo"

interface ComponentSelectorProps {
  component: any
  selections: DealSelections
  onSelectionChange: (componentId: string, selection: any) => void
}

function ComponentSelector({ component, selections, onSelectionChange }: ComponentSelectorProps) {
  const componentSelection = selections[component.id]

  const handleChoiceChange = (optionId: string) => {
    onSelectionChange(component.id, { optionId })
  }

  const handleMultiChoiceChange = (optionId: string, checked: boolean) => {
    const currentOptionIds = componentSelection?.optionIds || []
    const newOptionIds = checked
      ? [...currentOptionIds, optionId]
      : currentOptionIds.filter((id: string) => id !== optionId)

    onSelectionChange(component.id, { optionIds: newOptionIds })
  }

  const handleOptionalToggle = (checked: boolean) => {
    if (checked) {
      onSelectionChange(component.id, { optionIds: [component.options[0].id] })
    } else {
      onSelectionChange(component.id, { optionIds: [] })
    }
  }

  const formatOption = (option: any) => {
    const quantity = option.quantity || 1
    const unit = option.unit || "serving"
    return `${quantity} ${unit}${quantity !== 1 ? "s" : ""}`
  }

  switch (component.componentType as ComponentType) {
    case "FIXED":
      return (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Pre-selected items:</p>
          <div className="flex flex-wrap gap-2">
            {component.options.map((option: any) => (
              <Badge key={option.id} variant="secondary">
                {option.name} ({formatOption(option)})
                {option.price > 0 && ` +₦${option.price}`}
              </Badge>
            ))}
          </div>
        </div>
      )

    case "CHOICE":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">Choose one option:</p>
          <RadioGroup
            value={componentSelection?.optionId || ""}
            onValueChange={handleChoiceChange}
          >
            {component.options
              .filter((option: any) => option.inStock)
              .map((option: any) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.id} id={option.id} />
                  <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <span>{option.name}</span>
                      <div className="text-sm text-muted-foreground">
                        {formatOption(option)}
                        {option.price > 0 && ` (+₦${option.price})`}
                      </div>
                    </div>
                    {option.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {option.description}
                      </p>
                    )}
                  </Label>
                </div>
              ))}
          </RadioGroup>
        </div>
      )

    case "MULTI_CHOICE":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">Choose multiple options:</p>
          {component.options
            .filter((option: any) => option.inStock)
            .map((option: any) => (
              <div key={option.id} className="flex items-start space-x-2">
                <Checkbox
                  id={option.id}
                  checked={componentSelection?.optionIds?.includes(option.id) || false}
                  onCheckedChange={(checked) =>
                    handleMultiChoiceChange(option.id, checked as boolean)
                  }
                />
                <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span>{option.name}</span>
                    <div className="text-sm text-muted-foreground">
                      {formatOption(option)}
                      {option.price > 0 && ` (+₦${option.price})`}
                    </div>
                  </div>
                  {option.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {option.description}
                    </p>
                  )}
                </Label>
              </div>
            ))}
        </div>
      )

    case "OPTIONAL":
      const isSelected = componentSelection?.optionIds?.length !== undefined && componentSelection.optionIds.length > 0
      return (
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`optional-${component.id}`}
              checked={isSelected}
              onCheckedChange={handleOptionalToggle}
            />
            <Label htmlFor={`optional-${component.id}`} className="font-medium cursor-pointer">
              Add {component.name.toLowerCase()}
            </Label>
          </div>

          {isSelected && (
            <div className="ml-6 space-y-2">
              <p className="text-sm text-muted-foreground">Choose options:</p>
              {component.options
                .filter((option: any) => option.inStock)
                .map((option: any) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`optional-option-${option.id}`}
                      checked={componentSelection?.optionIds?.includes(option.id) || false}
                      onCheckedChange={(checked) =>
                        handleMultiChoiceChange(option.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={`optional-option-${option.id}`} className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <span>{option.name}</span>
                        <div className="text-sm text-muted-foreground">
                          {formatOption(option)}
                          {option.price > 0 && ` (+₦${option.price})`}
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
            </div>
          )}
        </div>
      )

    default:
      return null
  }
}

export default function DealDetailsScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { addDealToCart } = useCart()
  const [quantity, setQuantity] = useState(1)
  const [selections, setSelections] = useState<DealSelections>({})

  const { data: dealData, isLoading } = useGetDealQuery(id || "")
  const deal = dealData?.data

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(price)
  }

  const getDealTypeLabel = (dealType: string) => {
    switch (dealType) {
      case "FIXED":
        return "Fixed Deal"
      case "CONFIGURABLE":
        return "Customizable Deal"
      case "COMPLEX":
        return "Premium Deal"
      default:
        return dealType
    }
  }

  const calculateTotalPrice = () => {
    if (!deal) return 0

    let total = deal.price

    // Add extra costs from selected options
    for (const component of deal.components) {
      const componentSelection = selections[component.id]

      if (componentSelection) {
        if (componentSelection.optionId) {
          // Single choice
          const selectedOption = component.options.find(opt => opt.id === componentSelection.optionId)
          if (selectedOption) {
            total += selectedOption.price
          }
        } else if (componentSelection.optionIds) {
          // Multi choice or optional
          for (const optionId of componentSelection.optionIds) {
            const selectedOption = component.options.find(opt => opt.id === optionId)
            if (selectedOption) {
              total += selectedOption.price
            }
          }
        }
      }
    }

    return total * quantity
  }

  const validateSelections = () => {
    if (!deal) return false

    for (const component of deal.components) {
      const componentSelection = selections[component.id]

      if (component.required) {
        switch (component.componentType) {
          case "CHOICE":
            if (!componentSelection?.optionId) {
              toast.error(`Please select an option for ${component.name}`)
              return false
            }
            break
          case "MULTI_CHOICE":
            if (!componentSelection?.optionIds || componentSelection.optionIds.length === 0) {
              toast.error(`Please select at least one option for ${component.name}`)
              return false
            }
            break
        }
      }
    }

    return true
  }

  const handleSelectionChange = (componentId: string, selection: any) => {
    setSelections(prev => ({
      ...prev,
      [componentId]: selection
    }))
  }

  const handleAddToCart = () => {
    if (!deal) return

    if (!validateSelections()) return

    try {
      addDealToCart(deal.id, deal, selections, quantity)
      toast.success("Deal added to cart successfully!")
      navigate("/cart")
    } catch (error) {
      console.error("Failed to add deal to cart:", error)
      toast.error("Failed to add deal to cart")
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Deal not found</h3>
          <p className="text-muted-foreground">The deal you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/deals")} className="mt-4">
            Back to Deals
          </Button>
        </div>
      </div>
    )
  }

  const totalPrice = calculateTotalPrice()

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title={deal.name}
        description={deal.description}
        canonical={`/deal/${deal.id}`}
        image={deal.image}
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Deals", url: "/deals" },
          { name: deal.name, url: `/deal/${deal.id}` },
        ]}
      />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Button
          variant="ghost"
          onClick={() => navigate("/deals")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Deals
        </Button>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Deal Image */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative">
            <img
              src={deal.image}
              alt={deal.name}
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
            {deal.featured && (
              <Badge className="absolute top-4 left-4 bg-yellow-500 hover:bg-yellow-600">
                <Star className="w-3 h-3 mr-1 fill-current" />
                Featured
              </Badge>
            )}
            <Badge
              variant="secondary"
              className="absolute top-4 right-4"
            >
              {getDealTypeLabel(deal.dealType)}
            </Badge>
          </div>

          {/* Deal Images Gallery */}
          {deal.images && deal.images.length > 1 && (
            <div className="flex gap-2 mt-4 overflow-x-auto">
              {deal.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${deal.name} ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-lg cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Deal Details */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h1 className="text-3xl font-bold mb-2">{deal.name}</h1>
            <p className="text-muted-foreground text-lg">{deal.description}</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary">
                {formatPrice(totalPrice)}
              </span>
              {quantity > 1 && (
                <span className="text-sm text-muted-foreground">
                  ({formatPrice(deal.price)} each)
                </span>
              )}
            </div>
            {deal.originalPrice && deal.originalPrice > deal.price && (
              <span className="text-lg text-muted-foreground line-through">
                {formatPrice(deal.originalPrice)}
              </span>
            )}
          </div>

          {/* Quantity Selector */}
          <div className="flex items-center gap-4">
            <Label className="font-medium">Quantity:</Label>
            <div className="flex items-center border rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="px-4 py-2 font-medium">{quantity}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuantity(quantity + 1)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Components Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Customize Your Deal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {deal.components.map((component) => (
                <div key={component.id} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{component.name}</h4>
                    {component.required && (
                      <Badge variant="outline" className="text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                  {component.description && (
                    <p className="text-sm text-muted-foreground">
                      {component.description}
                    </p>
                  )}
                  <ComponentSelector
                    component={component}
                    selections={selections}
                    onSelectionChange={handleSelectionChange}
                  />
                  <Separator />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            className="w-full h-12 text-lg"
            size="lg"
          >
            <>
              <ShoppingCart className="w-5 h-5 mr-2" />
              Add to Cart
            </>
          </Button>
        </motion.div>
      </div>
    </div>
  )
}